'use client';

import { faCheck, faPaperPlane, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BetaBadge, Button } from '@ui';
import { StyledInput } from '@ui/Form';
import { useClubSlug } from 'components/Clubs/hooks';
import { AnimatePresence, motion } from 'framer-motion';
import { useApi } from 'hooks/useApi';
import React, { KeyboardEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { FormField } from './FormAssistent.types';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface GeneratedForm {
    name: string;
    mandatory_user_account: boolean;
    fields: FormField[];
}

interface ChatMessage {
    id: string;
    role: 'user' | 'model';
    text: string;
    generatedForm?: GeneratedForm;
}

interface HistoryItem {
    userQuestion: string;
    AIResponse: string;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const EXAMPLE_PROMPTS = [
    'Anmeldeformular mit Vor- und Nachname, E-Mail und AGB-Checkbox',
    'Sportverein-Formular mit Trikotgröße S/M/L und Zahlungsoptionen',
    'Trainingsplan-Formular mit Geburtsdatum und Wochentag-Auswahl'
];

const TYPE_LABELS: Record<string, string> = {
    text: 'Text',
    date: 'Datum',
    iban: 'IBAN',
    checkbox: 'Kästchen',
    select: 'Auswahl',
    payment_options: 'Zahlung',
    wysiwyg: 'Textblock',
    table: 'Tabelle',
    legal: 'Rechtliches'
};

const panelSurfaceClassName = 'tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-gap-3 tw-p-3';
const subtleSurfaceClassName = 'tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white';
const quietSurfaceClassName = 'tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2';

// ── Sub-components ─────────────────────────────────────────────────────────────

const ThinkingDots = () => (
    <div className="tw-flex tw-justify-start">
        <div className={`tw-rounded-2xl tw-rounded-bl-none tw-p-4 ${quietSurfaceClassName}`}>
            <div className="tw-flex tw-items-center tw-gap-1">
                {[0, 1, 2].map((i) => (
                    <motion.div
                        key={i}
                        className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-success"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.55, delay: i * 0.14, repeat: Infinity, ease: 'easeInOut' }}
                    />
                ))}
            </div>
        </div>
    </div>
);

const EmptyState = ({ onAsk }: { onAsk: (q: string) => void }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, ease: 'easeOut' }}
        className="tw-space-y-4 tw-text-center tw-text-accents-7"
    >
        <div className="tw-space-y-2">
            <p className="tw-m-0 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-accents-5">
                Beispielanfragen
            </p>
            {EXAMPLE_PROMPTS.map((q, i) => (
                <motion.div
                    key={q}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.06 + i * 0.06 }}
                >
                    <Button flat color="success" block onPress={() => onAsk(q)}>
                        {q}
                    </Button>
                </motion.div>
            ))}
        </div>
    </motion.div>
);

const GeneratedFormCard = ({ form }: { form: GeneratedForm }) => (
    <div className="tw-mt-4 tw-rounded-2xl tw-border tw-border-solid tw-border-success/25 tw-bg-successLight/70 tw-p-4">
        <div className="tw-mb-2 tw-flex tw-items-center tw-gap-2">
            <FontAwesomeIcon icon={faCheck} className="tw-text-xs tw-text-success" />
            <p className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-success">
                Übernommen
            </p>
        </div>
        <p className="tw-m-0 tw-mb-3 tw-text-xs tw-text-accents-5">
            {form.name || 'Formular'} · {form.fields.length} {form.fields.length === 1 ? 'Feld' : 'Felder'}
        </p>
        <div className="tw-flex tw-flex-wrap tw-gap-2">
            {form.fields.slice(0, 5).map((f, i) => (
                <span
                    key={i}
                    className="tw-rounded-full tw-border tw-border-solid tw-border-success/20 tw-bg-white tw-px-2 tw-py-1 tw-text-xs tw-text-accents-7"
                >
                    {TYPE_LABELS[f.type] ?? f.type}
                    {f.label ? `: ${f.label.slice(0, 16)}${f.label.length > 16 ? '…' : ''}` : ''}
                </span>
            ))}
            {form.fields.length > 5 && (
                <span className="tw-text-xs tw-text-accents-4">+{form.fields.length - 5} weitere</span>
            )}
        </div>
    </div>
);

const MessageBubble = ({ message }: { message: ChatMessage }) => {
    const isUser = message.role === 'user';

    return (
        <div className={`tw-flex ${isUser ? 'tw-justify-end' : 'tw-justify-start'}`}>
            <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className={[
                    'tw-w-fit tw-max-w-xl tw-rounded-2xl tw-p-3 tw-text-sm tw-shadow-sm',
                    isUser
                        ? 'tw-rounded-br-none tw-bg-success tw-text-white'
                        : 'tw-rounded-bl-none tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-0 tw-text-accents-9'
                ].join(' ')}
            >
                <p className="tw-m-0 tw-leading-5">{message.text}</p>
                {message.generatedForm && <GeneratedFormCard form={message.generatedForm} />}
            </motion.div>
        </div>
    );
};
export interface FormKIAssistantProps {
    onApply: (form: GeneratedForm) => void;
    currentForm: GeneratedForm;
}

export const FormKIAssistant: React.FC<FormKIAssistantProps> = ({ onApply, currentForm }) => {
    const { t } = useTranslation('form');
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const conversationIdRef = useRef<string | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const clubSlug = useClubSlug();
    const apiUrl = (process.env.NEXT_PUBLIC_BACKEND_HOST || '') + `ai/buildForm/${clubSlug}`;
    const [messages, setMessages] = useState<ChatMessage[]>([]);

    const api = useApi();
    const chatMessages = messages;

    const scrollToBottom = () =>
        setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }), 80);

    const ensureConversationId = () => {
        if (!conversationIdRef.current) conversationIdRef.current = uuid();
        return conversationIdRef.current;
    };

    const buildHistory = (msgs: ChatMessage[]): HistoryItem[] => {
        const items: HistoryItem[] = [];
        for (let i = 0; i < msgs.length - 1; i++) {
            if (msgs[i].role === 'user' && msgs[i + 1]?.role === 'model') {
                items.push({
                    userQuestion: msgs[i].text,
                    AIResponse: msgs[i + 1].generatedForm ? JSON.stringify(msgs[i + 1].generatedForm) : msgs[i + 1].text
                });
            }
        }
        return items;
    };

    const send = async (overridePrompt?: string) => {
        const prompt = (overridePrompt ?? input).trim();
        if (!prompt || loading) return;

        setInput('');
        setLoading(true);

        const userMsg: ChatMessage = { id: uuid(), role: 'user', text: prompt };
        const updatedMessages = [...chatMessages, userMsg];
        setMessages(updatedMessages);
        scrollToBottom();

        await api({
            url: apiUrl,
            method: 'POST',
            data: {
                prompt,
                conversationId: ensureConversationId(),
                history: buildHistory(updatedMessages),
                currentForm
            },
            onSuccess: (data) => {
                const hasForm = data?.answer && Array.isArray(data.answer.fields) && data.answer.fields.length > 0;
                if (hasForm) onApply(data.answer);
                const modelMsg: ChatMessage = {
                    id: uuid(),
                    role: 'model',
                    text: hasForm
                        ? `Formular mit ${data.answer.fields.length} ${data.answer.fields.length === 1 ? 'Feld' : 'Feldern'} übernommen. Du kannst es weiter verfeinern.`
                        : t('builder.aiFallbackMessage'),
                    generatedForm: hasForm ? data.answer : undefined
                };
                setMessages((prev) => [...(prev || []), modelMsg]);
                scrollToBottom();
                setLoading(false);
            },
            onError: () => {
                setMessages((prev) => [
                    ...(prev || []),
                    { id: uuid(), role: 'model', text: 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.' }
                ]);
                scrollToBottom();
                setLoading(false);
            }
        });
    };

    const handleClear = () => {
        setMessages([]);
        conversationIdRef.current = null;
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            void send();
        }
    };

    return (
        <div className={panelSurfaceClassName}>
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-2 tw-pt-1">
                <div className="tw-flex tw-min-w-0 tw-flex-col">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-align-middle">
                        <span className="tw-truncate tw-font-bold tw-text-accents-9">Formular KI Assistent</span>
                        <BetaBadge />
                    </div>
                    <span className="tw-text-xs tw-text-accents-6">
                        Beschreibt Formulare, erstellt Felder und übernimmt sie direkt.
                    </span>
                </div>
                <Button
                    flat
                    size="xs"
                    onPress={handleClear}
                    disabled={loading || chatMessages.length === 0}
                    className="!tw-h-8 !tw-rounded-full"
                    color="light"
                >
                    <FontAwesomeIcon icon={faTrash} />
                </Button>
            </div>

            <div className={`tw-relative tw-min-h-0 tw-flex-1 tw-overflow-hidden ${subtleSurfaceClassName}`}>
                <div
                    ref={listRef}
                    className="tw-h-full tw-space-y-5 tw-overflow-y-auto tw-scroll-smooth tw-p-4 md:tw-p-5"
                >
                    <AnimatePresence>
                        {chatMessages.length === 0 && !loading && (
                            <EmptyState key="empty" onAsk={(q) => void send(q)} />
                        )}
                    </AnimatePresence>

                    {chatMessages.map((msg) => (
                        <MessageBubble key={msg.id} message={msg} />
                    ))}

                    {loading && <ThinkingDots />}
                </div>
            </div>

            <div className="tw-flex tw-items-center tw-gap-2">
                <StyledInput
                    ref={inputRef}
                    name="form-ai-prompt"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                        chatMessages.length > 0 ? 'Verfeinern oder weiteres hinzufügen…' : 'Beschreibe dein Formular…'
                    }
                    fullWidth
                    flush
                    size="md"
                    disabled={loading}
                />
                <Button
                    size="md"
                    color="success"
                    isDisabled={!input.trim() || loading}
                    onPress={() => void send()}
                    className="!tw-h-10 !tw-min-w-10 !tw-rounded-xl !tw-px-3"
                >
                    <FontAwesomeIcon icon={faPaperPlane} />
                </Button>
            </div>
            <p className="tw-m-0 tw-px-1 tw-text-center tw-text-xs tw-italic tw-text-accents-6">
                KI kann Fehler machen. Bitte Felder, Labels und Regeln vor dem Speichern prüfen.
            </p>
        </div>
    );
};
